export interface Medication {
  id: string;
  name: string;
  dose: string;
  frequency: string;
}

export interface MentalStatusExam {
  appearance: string;
  behavior: string;
  speech: string;
  mood: string;
  affect: string;
  thoughtProcess: string;
  thoughtContent: string;
  cognition: string;
  insightJudgment: string;
}

export interface PatientData {
  // Demographics
  patientName: string;
  dateOfBirth: string;
  mrn: string;
  dateOfService: string;
  provider: string;

  // Clinical Information
  chiefComplaint: string;
  historyOfPresentIllness: string;

  // Symptoms
  symptoms: string[];

  // Medications
  medications: Medication[];

  // Mental Status Exam
  mentalStatusExam: MentalStatusExam;

  // Assessment & Plan
  assessment: string;
  plan: string;
}

export interface GenerateNoteResponse {
  note: string;
  generatedAt: string;
}

// Default/initial values
export const DEFAULT_MENTAL_STATUS_EXAM: MentalStatusExam = {
  appearance: '',
  behavior: '',
  speech: '',
  mood: '',
  affect: '',
  thoughtProcess: '',
  thoughtContent: '',
  cognition: '',
  insightJudgment: ''
};

export const DEFAULT_PATIENT_DATA: PatientData = {
  patientName: '',
  dateOfBirth: '',
  mrn: '',
  dateOfService: new Date().toISOString().split('T')[0],
  provider: 'Demo Provider, MD',
  chiefComplaint: '',
  historyOfPresentIllness: '',
  symptoms: [],
  medications: [],
  mentalStatusExam: DEFAULT_MENTAL_STATUS_EXAM,
  assessment: '',
  plan: ''
};

// Symptom options for the checklist
export const SYMPTOM_OPTIONS = [
  'Depressed mood',
  'Anhedonia (loss of interest/pleasure)',
  'Sleep disturbance',
  'Appetite changes',
  'Fatigue/low energy',
  'Difficulty concentrating',
  'Feelings of worthlessness/guilt',
  'Psychomotor agitation/retardation',
  'Suicidal ideation',
  'Anxiety',
  'Panic attacks',
  'Racing thoughts',
  'Decreased need for sleep',
  'Grandiosity',
  'Increased goal-directed activity',
  'Impulsivity',
  'Irritability',
  'Hallucinations (auditory)',
  'Hallucinations (visual)',
  'Paranoid ideation',
  'Obsessive thoughts',
  'Compulsive behaviors',
  'Flashbacks/intrusive memories',
  'Hypervigilance',
  'Avoidance behaviors',
  'Social withdrawal'
];

// Mental Status Exam options
export const MSE_OPTIONS = {
  appearance: [
    'Well-groomed',
    'Casually dressed',
    'Disheveled',
    'Unkempt',
    'Age-appropriate',
    'Appears older than stated age',
    'Appears younger than stated age'
  ],
  behavior: [
    'Cooperative',
    'Guarded',
    'Hostile',
    'Agitated',
    'Calm',
    'Withdrawn',
    'Engaged',
    'Poor eye contact',
    'Good eye contact'
  ],
  speech: [
    'Normal rate and rhythm',
    'Pressured',
    'Slow',
    'Soft',
    'Loud',
    'Monotone',
    'Tangential',
    'Circumstantial'
  ],
  mood: [
    'Euthymic',
    'Depressed',
    'Anxious',
    'Irritable',
    'Euphoric',
    'Dysphoric',
    'Angry'
  ],
  affect: [
    'Congruent with mood',
    'Flat',
    'Blunted',
    'Labile',
    'Restricted',
    'Full range',
    'Incongruent with mood'
  ],
  thoughtProcess: [
    'Linear and goal-directed',
    'Tangential',
    'Circumstantial',
    'Loose associations',
    'Flight of ideas',
    'Thought blocking',
    'Perseverative'
  ],
  thoughtContent: [
    'Denies SI/HI',
    'Passive suicidal ideation',
    'Active suicidal ideation without plan',
    'Active suicidal ideation with plan',
    'Homicidal ideation',
    'Paranoid ideation',
    'Delusions present',
    'No delusions',
    'Obsessions present'
  ],
  cognition: [
    'Alert and oriented x3',
    'Alert and oriented x4',
    'Oriented to person only',
    'Oriented to person and place',
    'Confused',
    'Memory intact',
    'Memory impaired'
  ],
  insightJudgment: [
    'Good',
    'Fair',
    'Poor',
    'Limited',
    'Intact'
  ]
};

// Frequency options for medications
export const MEDICATION_FREQUENCIES = [
  'Once daily',
  'Twice daily',
  'Three times daily',
  'Four times daily',
  'Every morning',
  'Every evening',
  'At bedtime',
  'As needed (PRN)',
  'Weekly',
  'Every other day'
];
